'use client';

import { useEffect } from 'react';

interface FaviconOptions {
    isImpersonating: boolean;
}

/**
 * Custom hook to dynamically update the favicon based on impersonation status
 */
export function useDynamicFavicon({ isImpersonating }: FaviconOptions) {
    useEffect(() => {
        // Only run on client side
        if (typeof window === 'undefined') return;

        const updateFavicon = () => {
            try {
                // Remove all existing favicon links to avoid conflicts
                const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
                existingFavicons.forEach(link => {
                    if (link.parentNode) {
                        link.parentNode.removeChild(link);
                    }
                });

                // Determine the correct favicon based on impersonation status
                const faviconPath = isImpersonating
                    ? '/favicon/image-login-as.png'
                    : process.env.NODE_ENV !== 'production'
                      ? '/favicon/tamnuni-dev-favicon.png'
                      : '/favicon/tamnuni-favicon.png';

                // Create new favicon link elements for better browser compatibility
                const createFaviconLink = (rel: string, sizes?: string) => {
                    const link = document.createElement('link');
                    link.rel = rel;
                    link.type = 'image/png';
                    link.href = `${faviconPath}?v=${Date.now()}`; // Add cache busting
                    if (sizes) link.setAttribute('sizes', sizes);
                    return link;
                };

                // Add multiple favicon formats for better browser support
                const faviconLinks = [
                    createFaviconLink('icon', '32x32'),
                    createFaviconLink('shortcut icon'),
                    createFaviconLink('apple-touch-icon', '180x180'),
                ];

                // Append all favicon links to head
                faviconLinks.forEach((link, index) => {
                    if (document.head) {
                        document.head.appendChild(link);
                    }
                });
            } catch (error) {
                console.error('❌ Error updating favicon:', error);
            }
        };

        // Also update the title to indicate impersonation status
        const updateTitle = () => {
            try {
                const baseTitle = 'Dashboard';
                const newTitle = isImpersonating ? `${baseTitle} - Impersonating` : baseTitle;
                document.title = newTitle;
            } catch (error) {
                console.error('❌ Error updating title:', error);
            }
        };

        // Run updates with a small delay to ensure DOM is ready
        const timeoutId = setTimeout(() => {
            updateFavicon();
            updateTitle();
        }, 50);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [isImpersonating]);
}
